// ─── Generic column header (sort + enum/range/date filter) ──────────────────
//
// Generalized from `@repo/ui`'s existing `TableSearchSortFilter` — adds
// `range` (min/max number inputs + a RangeSlider) and `date` (date-range
// picker) filter modes alongside the existing `enum` mode (multi-select
// checkboxes with counts), and drops the milestone-sequence-specific
// ordering logic (that stays local to TransactionTable's own usage of
// TableSearchSortFilter).

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Checkbox,
  CloseButton,
  Group,
  NumberInput,
  Popover,
  RangeSlider,
  Stack,
  Text,
  Tooltip,
  UnstyledButton
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowsSort, IconFilter, IconListTree, IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import { DATE_LASTDAYS_FILTER_SUFFIX, DATE_RANGE_FILTER_SUFFIX, FilterValue, isRangeFilterValue } from './types';

/**
 * Props for the DataGridColumnHeader component and its internal sub-components.
 */
export interface DataGridColumnHeaderProps {
  columnKey: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: 'enum' | 'range' | 'date';
  sortColumns: string[];
  sortOrders: ('asc' | 'desc' | 'none')[];
  onSort: (columnKey: string, order: 'asc' | 'desc' | 'none') => void;
  /** Enum mode: available option values. */
  filterOptions?: string[];
  /** Enum mode: per-option row counts. */
  filterCounts?: Record<string, number>;
  /** Enum mode: customizes each option's label content (icon/color swatch/etc.) — see `ColumnMeta.renderFilterOption`. */
  renderFilterOption?: (optionValue: string) => ReactNode;
  /** Range mode: [min, max] bounds across the current row set, used to size the slider. Falls back to [0, 100] when not provided. */
  domain?: { min: number; max: number };
  currentFilter?: FilterValue;
  dateRangeFilter?: FilterValue;
  lastDaysFilter?: FilterValue;
  onFilterChange: (columnKey: string, value: FilterValue) => void;
  /** Whether this column may be selected as the row-grouping column (see `ColumnMeta.groupable`). */
  groupable?: boolean;
  /** Whether this column is currently the active group-by column. */
  isGrouped?: boolean;
  /** Toggles this column as the group-by column (starts grouping, or stops it if already grouping). */
  onToggleGroup?: (columnKey: string) => void;
}

/**
 * Internal component that renders a sort indicator icon (unsorted, ascending, or descending) with click-to-cycle logic scoped to this column.
 *
 * Cycles in the order: none → asc → desc → none on successive clicks.
 */
function SortIcon({
  columnKey,
  sortColumns,
  sortOrders,
  onSort,
}: Pick<DataGridColumnHeaderProps, 'columnKey' | 'sortColumns' | 'sortOrders' | 'onSort'>) {
  const index = sortColumns.indexOf(columnKey);
  const order = index !== -1 ? (sortOrders[index] ?? 'none') : 'none';
  const isActive = index !== -1;
  const rank = isActive ? index + 1 : undefined;
  const direction = order === 'asc' ? 'ascending' : order === 'desc' ? 'descending' : 'unsorted';

  return (
    <ActionIcon
      variant='default'
      size='15'
      aria-label={`Sort ${direction}${rank ? `, priority ${rank}` : ''}`}
      style={{ border: 'none', position: 'relative' }}
      onClick={() => onSort(columnKey, order === 'none' ? 'asc' : order === 'asc' ? 'desc' : 'none')}
    >
      {order === 'none' && <IconArrowsSort size={14} />}
      {order === 'asc' && (
        <IconSortDescending size={14} color={isActive ? 'blue' : 'black'} />
      )}
      {order === 'desc' && (
        <IconSortAscending size={14} color={isActive ? 'blue' : 'black'} />
      )}
      {rank && <Text component='span' fz={8} fw={700} style={{ position: 'absolute', right: -2, bottom: -3 }}>{rank}</Text>}
    </ActionIcon>
  );
}

/**
 * Internal component that renders an enum-type filter popover: checkboxes with per-option row counts, a single-select "only" button per option, and a reset button.
 *
 * When the user has not explicitly excluded options, every checkbox shows checked — until they uncheck something, "everything selected" and "no filter" are the same thing.
 */
function EnumFilterPopover({
  columnKey,
  filterOptions = [],
  filterCounts = {},
  currentFilter,
  onFilterChange,
  renderFilterOption,
}: Pick<DataGridColumnHeaderProps, 'columnKey' | 'filterOptions' | 'filterCounts' | 'currentFilter' | 'onFilterChange' | 'renderFilterOption'>) {
  // Matches the original (non-DataGrid) TransactionTable's universal
  // `workingPreset.filters[key] || opts.key` convention, used for every one
  // of its filter columns: until the user has actually excluded something,
  // every option is implicitly "selected" — every checkbox shows checked —
  // since "everything selected" and "no filter" are the same thing for the
  // actual row-matching logic (filterGridItems/matchesEnumFilter in
  // gridEngine.ts already treat a missing/empty filter entry as "no active
  // filter", i.e. show all rows). Once the user unchecks anything,
  // `currentFilter` becomes a real, explicit, non-empty array and this
  // fallback stops applying — checkboxes then reflect exactly that array.
  const hasExplicitSelection = Array.isArray(currentFilter) && currentFilter.length > 0;
  const selected = hasExplicitSelection ? currentFilter : filterOptions;
  const isFiltered = hasExplicitSelection;
  const [hovered, setHovered] = useState<string | null>(null);

  const toggle = (option: string, checked: boolean) => {
    const lower = option.toLowerCase();
    const next = checked ? [...selected, lower] : selected.filter((v) => v.toLowerCase() !== lower);
    onFilterChange(columnKey, next);
  };

  return (
    <Popover width={220} withArrow>
      <Popover.Target>
        <ActionIcon variant='default' size='15' aria-label='filter' style={{ border: 'none' }}>
          <IconFilter size={14} color={isFiltered ? 'var(--mantine-color-blue-filled)' : 'var(--mantine-color-text)'} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown style={{ maxHeight: 280, overflowY: 'auto', padding: 8 }}>
        <Stack gap={4}>
          <UnstyledButton onClick={() => onFilterChange(columnKey, [])} style={{ alignSelf: 'flex-end' }}>
            <Text fz='xs' c='dimmed' td='underline'>
              Reset
            </Text>
          </UnstyledButton>
          {filterOptions.map((option) => {
            const checked = selected.map((v) => v.toLowerCase()).includes(option.toLowerCase());
            const count = filterCounts[option] ?? filterCounts[option.toLowerCase()];
            const isHovered = hovered === option;
            return (
              <div
                key={option}
                onMouseEnter={() => setHovered(option)}
                onMouseLeave={() => setHovered(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}
              >
                <Checkbox
                  checked={checked}
                  label={
                    renderFilterOption ? (
                      renderFilterOption(option)
                    ) : (
                      <Text fz='sm' span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {option}
                      </Text>
                    )
                  }
                  onChange={(e) => toggle(option, e.currentTarget.checked)}
                  styles={{ root: { flex: 1, minWidth: 0 }, label: { overflow: 'hidden' } }}
                />
                <UnstyledButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onFilterChange(columnKey, [option.toLowerCase()]);
                  }}
                  style={{
                    opacity: isHovered ? 1 : 0,
                    pointerEvents: isHovered ? 'auto' : 'none',
                    flexShrink: 0,
                    transition: 'opacity 120ms',
                  }}
                >
                  <Text fz='xs' c='blue' style={{ whiteSpace: 'nowrap' }}>
                    only
                  </Text>
                </UnstyledButton>
                {count !== undefined && (
                  <Text fz='xs' c='dimmed' style={{ flexShrink: 0, minWidth: 20, textAlign: 'right' }}>
                    {count}
                  </Text>
                )}
              </div>
            );
          })}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Internal component that renders a range filter popover: min/max number inputs and a RangeSlider with a committed value that buffers drag gestures locally.
 *
 * Uses localSliderValue to avoid the lag of async persistence — slider feels instant, actual filter persists onChangeEnd when the user releases.
 */
function RangeFilterPopover({
  columnKey,
  currentFilter,
  domain,
  onFilterChange,
}: Pick<DataGridColumnHeaderProps, 'columnKey' | 'currentFilter' | 'domain' | 'onFilterChange'>) {
  const range = isRangeFilterValue(currentFilter) ? currentFilter : {};
  const isFiltered = range.min != null || range.max != null;

  // Falls back to a generic [0, 100] domain if the caller doesn't provide
  // one (e.g. computed via `getNumericDomain` over the current row set) —
  // the slider is still usable, just not meaningfully bounded.
  const domainMin = domain?.min ?? 0;
  const domainMax = domain?.max ?? 100;
  const sliderValue: [number, number] = [range.min ?? domainMin, range.max ?? domainMax];

  const setRange = (next: { min?: number; max?: number }) => onFilterChange(columnKey, next);

  // The committed filter round-trips through an async preference-persist
  // mutation (see useGridPresets.ts), so `currentFilter`/`sliderValue` above
  // don't update in sync with a drag gesture. Without local buffering, the
  // slider's `value` prop stays pinned to the last-committed (network-slow)
  // filter while the user drags, making it look frozen. `localSliderValue`
  // tracks every drag frame instantly via `onChange`; the filter is only
  // actually persisted once via `onChangeEnd`, when the user releases.
  const [localSliderValue, setLocalSliderValue] = useState<[number, number]>(sliderValue);
  useEffect(() => {
    setLocalSliderValue(sliderValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.min, range.max, domainMin, domainMax]);

  return (
    <Popover width={240} withArrow>
      <Popover.Target>
        <ActionIcon variant='default' size='15' aria-label='filter' style={{ border: 'none' }}>
          <IconFilter size={14} color={isFiltered ? 'var(--mantine-color-blue-filled)' : 'var(--mantine-color-text)'} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={10}>
          <Group gap={6} grow>
            <NumberInput
              label='Min'
              size='xs'
              value={range.min ?? ''}
              onChange={(v) => setRange({ ...range, min: v === '' ? undefined : Number(v) })}
            />
            <NumberInput
              label='Max'
              size='xs'
              value={range.max ?? ''}
              onChange={(v) => setRange({ ...range, max: v === '' ? undefined : Number(v) })}
            />
          </Group>
          {domainMax > domainMin && (
            <RangeSlider
              min={domainMin}
              max={domainMax}
              value={localSliderValue}
              onChange={setLocalSliderValue}
              onChangeEnd={([min, max]) => setRange({ min, max })}
              label={(v) => v.toLocaleString()}
              size='sm'
              py={4}
            />
          )}
          <UnstyledButton onClick={() => setRange({})} style={{ alignSelf: 'flex-end' }}>
            <Text fz='xs' c='dimmed' td='underline'>
              Reset
            </Text>
          </UnstyledButton>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Internal component that renders a date-range filter popover using Mantine's DatePickerInput (range mode), converting between ISO date strings and epoch-ms internally.
 */
function DateFilterPopover({
  columnKey,
  currentFilter,
  dateRangeFilter,
  lastDaysFilter,
  onFilterChange,
}: Pick<DataGridColumnHeaderProps, 'columnKey' | 'currentFilter' | 'dateRangeFilter' | 'lastDaysFilter' | 'onFilterChange'>) {
  // Date ranges are stored as epoch-millisecond numbers in the same
  // `{min?, max?}` shape used by numeric range filters, so FilterValue
  // doesn't need a third shape — just a different UI to produce/consume it.
  // Mantine's DatePickerInput (range) works with ISO date strings
  // ('YYYY-MM-DD'), so we convert at the edges.
  const range = isRangeFilterValue(dateRangeFilter) ? dateRangeFilter : {};
  const lastDays = Array.isArray(lastDaysFilter) ? Number(lastDaysFilter[0]) : undefined;
  const isFiltered = range.min != null || range.max != null || (lastDays != null && Number.isFinite(lastDays));

  const toDateString = (ms: number | undefined): string | null => (ms != null ? new Date(ms).toISOString().slice(0, 10) : null);
  const fromDateString = (value: string | null): number | undefined => (value ? new Date(value).getTime() : undefined);

  const value: [string | null, string | null] = [toDateString(range.min), toDateString(range.max)];

  const handleChange = ([start, end]: [string | null, string | null]) => {
    onFilterChange(`${columnKey}${DATE_RANGE_FILTER_SUFFIX}`, {
      min: fromDateString(start),
      max: fromDateString(end),
    });
  };

  return (
    <Popover width={280} withArrow>
      <Popover.Target>
        <ActionIcon variant='default' size='15' aria-label='filter' style={{ border: 'none' }}>
          <IconFilter size={14} color={isFiltered ? 'var(--mantine-color-blue-filled)' : 'var(--mantine-color-text)'} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={6}>
          <DatePickerInput type='range' label='Date range' size='xs' value={value} onChange={handleChange} clearable allowSingleDateInRange />
          <NumberInput
            size='xs'
            label='Last N days'
            placeholder='e.g. 30'
            min={1}
            value={Number.isFinite(lastDays) ? lastDays : ''}
            onChange={(value) => {
              const n = Number(value);
              onFilterChange(`${columnKey}${DATE_LASTDAYS_FILTER_SUFFIX}`, value === '' || !Number.isFinite(n) || n <= 0 ? [] : [String(n)]);
            }}
          />
          <UnstyledButton onClick={() => {
            onFilterChange(`${columnKey}${DATE_RANGE_FILTER_SUFFIX}`, {});
            onFilterChange(`${columnKey}${DATE_LASTDAYS_FILTER_SUFFIX}`, []);
          }} style={{ alignSelf: 'flex-end' }}>
            <Text fz='xs' c='dimmed' td='underline'>
              Reset
            </Text>
          </UnstyledButton>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Internal component that renders a "Group by this column" icon button with a tooltip, toggling this column as the active group-by column.
 */
function GroupToggleIcon({
  columnKey,
  onToggleGroup,
}: Pick<DataGridColumnHeaderProps, 'columnKey' | 'onToggleGroup'>) {
  // Only ever rendered for the "not yet grouped, click to start grouping"
  // state — once a column becomes the active group-by column, its label
  // switches to a removable Badge instead (see `DataGridColumnHeader` below),
  // which is the primary way to ungroup.
  return (
    <Tooltip label='Group by this column' withArrow>
      <ActionIcon
        variant='default'
        size='15'
        aria-label='group by this column'
        style={{ border: 'none' }}
        onClick={() => onToggleGroup?.(columnKey)}
      >
        <IconListTree size={14} color='var(--mantine-color-text)' />
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * A column header with sort, filter (enum/range/date), and group-by toggle controls — renders as a group badge when actively grouped.
 */
export function DataGridColumnHeader({
  columnKey,
  label,
  sortable,
  filterable,
  filterType = 'enum',
  sortColumns,
  sortOrders,
  onSort,
  filterOptions,
  filterCounts,
  domain,
  currentFilter,
  dateRangeFilter,
  lastDaysFilter,
  onFilterChange,
  renderFilterOption,
  groupable,
  isGrouped,
  onToggleGroup,
}: DataGridColumnHeaderProps) {
  return (
    <Group gap={6} wrap='nowrap'>
      {isGrouped ? (
        <Tooltip label='Stop grouping by this column' withArrow>
          <Badge
            variant='light'
            color='blue'
            size='lg'
            radius='sm'
            style={{ textTransform: 'none', cursor: 'default' }}
            rightSection={
              <CloseButton
                size={14}
                variant='transparent'
                c='blue'
                aria-label='Stop grouping by this column'
                onClick={() => onToggleGroup?.(columnKey)}
              />
            }
          >
            {label}
          </Badge>
        </Tooltip>
      ) : (
        <Text fz='sm' fw={600} truncate>
          {label}
        </Text>
      )}
      <ActionIcon.Group style={{ gap: 5 }}>
        {sortable && <SortIcon columnKey={columnKey} sortColumns={sortColumns} sortOrders={sortOrders} onSort={onSort} />}
        {filterable && filterType === 'enum' && (
          <EnumFilterPopover
            columnKey={columnKey}
            filterOptions={filterOptions}
            filterCounts={filterCounts}
            currentFilter={currentFilter}
            onFilterChange={onFilterChange}
            renderFilterOption={renderFilterOption}
          />
        )}
        {filterable && filterType === 'range' && (
          <RangeFilterPopover columnKey={columnKey} currentFilter={currentFilter} domain={domain} onFilterChange={onFilterChange} />
        )}
        {filterable && filterType === 'date' && (
          <DateFilterPopover
            columnKey={columnKey}
            currentFilter={currentFilter}
            dateRangeFilter={dateRangeFilter}
            lastDaysFilter={lastDaysFilter}
            onFilterChange={onFilterChange}
          />
        )}
      </ActionIcon.Group>
      {groupable && !isGrouped && <GroupToggleIcon columnKey={columnKey} onToggleGroup={onToggleGroup} />}
    </Group>
  );
}

export default DataGridColumnHeader;
